/**
 * P17 — the browser Maps key and the two APIs it is scoped to.
 *
 * **`web/src/maps/` is P17's**, a fifth directory owner under `web/` alongside P6's shell, P7's
 * MVP screens, P9's Activity surfaces and P15's post-MVP ones (`AGENTS.md` §4.8). It holds the
 * client-side **Places** call and nothing else — **Geocoding was retired by D169 and its module
 * deleted by D170**, which answered the Q4 that D169(e) sequenced the removal behind; the control that uses them is a P7
 * screen extension and lives with the screens.
 *
 * **The key is public by design (D34) and the HTTP-referrer restriction *is* the control.** It is
 * therefore a build-time `VITE_` variable baked into the bundle, exactly like the API URL — never
 * a Secrets Manager entry, never fetched from an endpoint, and never described as a secret. There
 * is no server-side Maps key and no server-side geocoding Lambda anywhere in this design (D51 🔸),
 * which is a privacy control rather than an architecture preference: it is one of the mitigations
 * Q11 is bounded by, so routing these calls through the API to "protect" the key would weaken the
 * thing it looks like it protects.
 *
 * **An absent key is a supported state, not a misconfiguration.** Until HUMAN-GUIDE row 9 is
 * discharged the variable is unset in every stage, and the picker must degrade to free text with
 * the install still completing — that is [§13 "The address is required and the degraded path works (D51)"](../../../docs/prds/selogate-prd.md)'s
 * "Maps key blocked" clause, and it is reachable *by default* rather than only under fault
 * injection. `main.tsx`'s API-URL guard has no counterpart here for that reason.
 */

import { usesNativeMapsHttp } from './nativeFetch'

const env = import.meta.env as unknown as Readonly<Record<string, string | undefined>>

/**
 * The referrer-restricted browser key, **scoped to Places API (New) alone** (HUMAN-GUIDE row 9,
 * as amended by D169). It was scoped to Places *and* Geocoding on paper for sixteen days; that key
 * could not exist, because Google refuses referrer restrictions on web-service APIs.
 */
export const MAPS_API_KEY: string = (env['VITE_SELOGATE_MAPS_KEY'] ?? '').trim()

/**
 * **D260 — the iOS container's key, restricted to this app's bundle identifier rather than to a
 * referrer.**
 *
 * A key carries **one** restriction type, so this cannot be an extra entry on the browser key: the
 * iOS WebView's origin is `capacitor://<host>` (D161(c)), which an HTTP-referrer restriction cannot
 * express, and widening the browser key to reach iOS means removing its restriction — the one repair
 * HUMAN-GUIDE row 9 forbids by name. It is public by design exactly as its sibling is (**D34**: the
 * restriction *is* the control), so it is a build-time `VITE_` variable, never a Secrets Manager
 * entry and never described as a secret.
 *
 * **Unset is a supported state on this platform as on the others** — the picker degrades to free
 * text and the install still completes.
 *
 * **Neither key may reach the other surface**, and the failure is silent in both directions: a
 * bundle-ID key sent from a browser origin and a referrer key sent from `capacitor://<host>` are
 * both `403`, and 🔸 both render as the same `location.mapsUnavailable` string as an absent key, so
 * a crossed key cannot be told from an unconfigured one by looking at the app.
 */
export const MAPS_API_KEY_IOS: string = (env['VITE_SELOGATE_MAPS_KEY_IOS'] ?? '').trim()

/**
 * **The bundle identifier the iOS container claims to Google, and it is a CONSTANT rather than a
 * build input (D260, decided 2026-09-07).**
 *
 * Google validates an iOS-app restriction from this header, and **the app cannot discover its own
 * bundle identifier**: Xcode decides it per configuration (`Debug` is `com.selogate.app.dev`,
 * `Release` is `com.selogate.app`), the Capacitor bridge injects no such member, and there is no
 * `@capacitor/app` plugin to ask — adding one is the fourth-plugin cost **D260(f)** refuses. The
 * stage is **not** a proxy for it either: `native/RELEASING.md` archives *staging* with
 * `-configuration Release`, so a stage-derived guess would send `.dev` for a build whose real
 * identity is the store id.
 *
 * So the client sends the **store identity**, one value for every configuration. That keeps
 * **D260's stated inventory at one build input** and, more usefully, removes the failure v6.66
 * warned about from the client's side entirely: a Debug container reaches Places because it sends
 * the store id, whether or not the key also lists `.dev`.
 *
 * 🔸 **The cost, stated:** this header is a claim of identity that Google cannot verify for a REST
 * caller, and a Debug build makes the store's claim. Listing `com.selogate.app.dev` on the key
 * therefore buys *this* client nothing — it is still correct to list, since the association
 * documents and any future caller use the real id, but nothing here depends on it. **Do not
 * "correct" this to a per-configuration value without a build input to carry it**: there is no
 * source for one in the bundle, and a wrong guess is a `403` indistinguishable from an absent key.
 */
export const IOS_BUNDLE_IDENTIFIER = 'com.selogate.app'

/** 🔸 D51's pilot restriction, now bounding the one remaining API to Israel; `country:il` in the decision's words. */
export const MAPS_REGION_CODE = 'IL'

/**
 * The key this platform's Places call must present.
 *
 * **A function rather than a constant**, because the answer depends on a bridge that is injected
 * before app code runs but read at call time — the same reason `usesNativeHttp()` is read per call
 * rather than latched.
 */
export function mapsApiKey(): string {
  return usesNativeMapsHttp() ? MAPS_API_KEY_IOS : MAPS_API_KEY
}

/**
 * The headers that authorize one Places request.
 *
 * **The bundle-identifier header is sent on iOS only.** Sending it on the web would be harmless to
 * Google — a referrer-restricted key ignores it — but it would put a false claim on every browser
 * request and blur which restriction is actually being exercised when a `403` has to be diagnosed.
 */
export function mapsRequestHeaders(): Record<string, string> {
  return usesNativeMapsHttp()
    ? { 'X-Goog-Api-Key': MAPS_API_KEY_IOS, 'X-Ios-Bundle-Identifier': IOS_BUNDLE_IDENTIFIER }
    : { 'X-Goog-Api-Key': MAPS_API_KEY }
}

/**
 * Whether the one remaining network input may be offered at all. False is the degraded path, by
 * default — and it is now **per platform**, since an iOS build with no iOS key must degrade even
 * where the browser key is baked into the same bundle.
 */
export function mapsConfigured(): boolean {
  return mapsApiKey().length > 0
}

/**
 * Why a Maps call did not produce a usable answer.
 *
 * `denied` and `quota` are split because they are the two the Monitor's maps panel (M13) reports
 * separately and an operator acts on differently — a rejected referrer is a key misconfiguration
 * that fails every call from every client, while an exhausted quota is a billing event that
 * resolves on its own clock. Neither is distinguishable from `network` at the UI, which is why the
 * picker treats all four the same way and only the panel cares.
 */
export type MapsFailureReason = 'unconfigured' | 'denied' | 'quota' | 'network' | 'no-results'

export interface MapsFailure {
  readonly ok: false
  readonly reason: MapsFailureReason
}

/**
 * 🔸 The coordinate precision the row is stored at, mirroring
 * `packages/functions/provision/config.ts`'s `COORDINATE_DECIMALS`.
 *
 * Restated rather than imported for the reason `auth/config.ts`'s `MAX_NAME_GRAPHEMES` is: the
 * server's copy lives in a Lambda package the browser bundle must not pull in, and
 * `@selogate/core` — the one package both sides may share — does not carry the location
 * primitives. The server rounds again on receipt, so a drift here costs display honesty (the
 * technician would see more precision than is stored) rather than a wrong pin.
 */
export const COORDINATE_DECIMALS = 6

/** Round to the stored precision, so what the picker shows is what the gate record will hold. */
export function roundCoordinate(value: number): number {
  const factor = 10 ** COORDINATE_DECIMALS
  return Math.round(value * factor) / factor
}

/**
 * Classify a fetch/HTTP outcome. This was shared by two callers that disagreed about how they say
 * the same things — Places (New) answers with HTTP status codes, Geocoding answered `200` with a
 * `status` string — and **one caller is left since D170 removed the geocode**. It stays a function
 * rather than being inlined because the Monitor's maps panel (M13) reads the same taxonomy.
 */
export function httpFailureReason(status: number): MapsFailureReason {
  if (status === 401 || status === 403) return 'denied'
  if (status === 429) return 'quota'
  return 'network'
}
